import { PackFeedCard } from '@/components/pack-feed-card';
import { PackPackCard } from '@/components/pack-pack-card';
import { MediaPackType } from '@/utils/media/types';
import {
    MediaNestedPackTypeName,
    MediaPackGuideTypeName,
    MediaPackProgramTypeName,
} from '@calm/evexia-web-sdk';
import { CardVariant } from '@rhg/components/card/types';
import { PackGuideCard } from '@rhg/components/pack-guide-card';
import { PackProgramCard } from '@rhg/components/pack-program-card';
import { PaginationSentinel } from '@rhg/components/pagination-sentinel';
import {
    PlaceholderWhileOffScreen,
    PackPageSkeleton,
} from '@rhg/components/placeholder-while-offscreen';
import { GlobalGridItem } from '@rhg/components/ui/global-grid-item';
import { useClientPagination } from '@rhg/hooks/use-client-pagination';
import { useGridContext } from '@rhg/hooks/use-global-grid-context';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { Box } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { FEATURED_COLLECTIONS_CURATED_PACK_CLASS } from '@rhg/utils/pack-card';
import { useEffect, useState } from 'react';
import { MAX_CENTERED_ITEMS, styleCardFooter } from '..';

interface PackPageGridSectionProps {
    pack?: MediaPackType;
}

const styleGrid = {
    display: 'grid',
    width: '100%',
    '& > *': {
        minWidth: 0, // Prevents flex items from overflowing their container
    },
};

const styleCenterGrid = {
    justifyContent: 'center',
};

export const PackPageGridSection = ({ pack }: PackPageGridSectionProps) => {
    const [centerGridItems, setCenterGridItems] = useState(false);
    const { gutter, columns } = useGridContext();
    const isMobile = useIsMobile();
    // Eager-load the above-the-fold grid cards: 1 on mobile, first 3 on desktop.
    const eagerCount = isMobile ? 1 : 3;
    // Client-paginate so only a batch of cards mount at once — keeps per-card
    // API calls and IntersectionObservers bounded for very large packs.
    const { visibleItems, hasMore, isRevealing, sentinelRef } =
        useClientPagination(pack?.items ?? []);

    // Featured collections use 3:2 cards to match how they render in the
    // Discover carousel; every other pack page keeps the default 1:1 cards.
    const cardVariant =
        pack?.packClass === FEATURED_COLLECTIONS_CURATED_PACK_CLASS
            ? CardVariant.Carousel
            : CardVariant.CarouselSquare;

    // Default - account for program cards view all
    useEffect(() => {
        if (
            (pack?.items?.length ?? Number.POSITIVE_INFINITY) <=
            MAX_CENTERED_ITEMS
        ) {
            setCenterGridItems(true);
        }
    }, [pack?.items?.length]);

    return (
        <Box
            sx={sxAppend(styleGrid, centerGridItems && styleCenterGrid, {
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gap: `${gutter}px`,
            })}
        >
            {visibleItems.map((packItem, index) => {
                if (
                    'guideId' in packItem &&
                    packItem.type === MediaPackGuideTypeName.Guide
                ) {
                    return (
                        <GlobalGridItem key={packItem?.guideId} span={3}>
                            <PlaceholderWhileOffScreen
                                placeholder={<PackPageSkeleton />}
                                sx={{ height: '100%' }}
                            >
                                <PackGuideCard
                                    packClass={pack?.packClass ?? ''}
                                    variant={cardVariant}
                                    packGuide={packItem}
                                    sx={{ height: '100%' }}
                                    contentSx={styleCardFooter}
                                    priority={index < eagerCount}
                                />
                            </PlaceholderWhileOffScreen>
                        </GlobalGridItem>
                    );
                } else if (
                    'packClass' in packItem &&
                    packItem.type === MediaNestedPackTypeName.Pack
                ) {
                    return (
                        <GlobalGridItem key={packItem?.packClass} span={3}>
                            <PlaceholderWhileOffScreen
                                placeholder={<PackPageSkeleton />}
                                sx={{ height: '100%' }}
                            >
                                <PackPackCard
                                    pack={packItem}
                                    cardVariant={cardVariant}
                                    cardSx={{ height: '100%' }}
                                    cardContentSx={styleCardFooter}
                                    priority={index < eagerCount}
                                />
                            </PlaceholderWhileOffScreen>
                        </GlobalGridItem>
                    );
                } else if (
                    'programId' in packItem &&
                    packItem.type === MediaPackProgramTypeName.Program
                ) {
                    return (
                        <GlobalGridItem key={packItem?.programId} span={3}>
                            <PlaceholderWhileOffScreen
                                placeholder={<PackPageSkeleton />}
                                sx={{ height: '100%' }}
                            >
                                <PackProgramCard
                                    packClass={pack?.packClass ?? ''}
                                    variant={cardVariant}
                                    packProgram={packItem}
                                    sx={{ height: '100%' }}
                                    contentSx={styleCardFooter}
                                    priority={index < eagerCount}
                                />
                            </PlaceholderWhileOffScreen>
                        </GlobalGridItem>
                    );
                } else if ('feedId' in packItem && packItem.type === 'feed') {
                    return (
                        <GlobalGridItem key={packItem?.feedId} span={3}>
                            <PlaceholderWhileOffScreen
                                placeholder={<PackPageSkeleton />}
                                sx={{ height: '100%' }}
                            >
                                <PackFeedCard
                                    feed={packItem}
                                    cardVariant={cardVariant}
                                    cardSx={{ height: '100%' }}
                                    cardContentSx={styleCardFooter}
                                    priority={index < eagerCount}
                                />
                            </PlaceholderWhileOffScreen>
                        </GlobalGridItem>
                    );
                } else {
                    return null;
                }
            })}
            {hasMore && (
                <PaginationSentinel ref={sentinelRef} isLoading={isRevealing} />
            )}
        </Box>
    );
};
